import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import {
  FileText, Search, SkipForward, CheckCircle2, XCircle, Loader2,
  PoundSterling, ArrowLeft, ChevronRight, Send,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { XeroInvoiceDialog } from './XeroInvoiceDialog';

/**
 * Multi-step dialog shown after creating a new customer.
 * Step 1: Choose an action — send invoice, find existing in Xero, skip
 * Step 2 (skip path): Has the customer paid?
 * Step 3 (paid path): Which invoice (deposit/final) + Xero invoice number
 */
export const PostCreateInvoiceDialog = ({ open, customer, onClose, defaultInvoiceType = 'deposit', triggerLabel = null }) => {
  const [step, setStep] = useState('action');
  // step states: 'action' | 'search' | 'paid' | 'reconcile' | 'done'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [showXeroDialog, setShowXeroDialog] = useState(false);

  // Reconcile state
  const [paidInvoiceType, setPaidInvoiceType] = useState(defaultInvoiceType);
  const [paidInvoiceNumber, setPaidInvoiceNumber] = useState('');
  const [paidAmount, setPaidAmount] = useState('');

  useEffect(() => {
    if (open) {
      setStep('action');
      setSearchQuery('');
      setSearchResults([]);
      setPaidInvoiceType(defaultInvoiceType);
      setPaidInvoiceNumber('');
      setPaidAmount('');
    }
  }, [open, defaultInvoiceType]);

  if (!customer) return null;

  const customerName = `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
  const depositAmount = customer.deposit_amount || 0;
  const finalAmount = customer.final_bill_amount || customer.system_total_cost || 0;
  const isFinal = defaultInvoiceType === 'final';
  const focusAmount = isFinal ? finalAmount : depositAmount;
  const focusLabel = isFinal ? 'Final payment' : 'Deposit';

  const closeAll = () => {
    setStep('action');
    onClose?.();
  };

  /* ---- Step 1 — Action ---- */
  const handleSendInvoice = () => {
    setShowXeroDialog(true);
  };

  const runSearch = async (qInit) => {
    setSearchLoading(true);
    try {
      const r = await api.get('/xero/invoices/search', {
        params: { q: qInit ?? searchQuery, customer_id: customer.id, limit: 25 },
      });
      setSearchResults(r.data?.invoices || []);
      if ((r.data?.invoices || []).length === 0) {
        toast.info('No matching invoices found in Xero');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Xero search failed');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const goToSearch = () => {
    setStep('search');
    // Auto-search for this customer's existing invoices
    runSearch('');
  };

  /* ---- Step 2 — Has paid? ---- */
  const handleHasPaid = (paid) => {
    if (paid) {
      setStep('reconcile');
    } else {
      closeAll();
    }
  };

  /* ---- Reconcile + Link helpers ---- */
  const linkInvoice = async ({ xero_invoice_id, invoice_number, amount, mark_paid, invoice_type }) => {
    setLinking(true);
    try {
      const r = await api.post(`/customers/${customer.id}/xero/link-invoice`, {
        invoice_type,
        xero_invoice_id: xero_invoice_id || null,
        invoice_number: invoice_number || null,
        amount: amount || null,
        mark_paid: !!mark_paid,
      });
      toast.success(`Linked ${invoice_type} invoice ${r.data.invoice_number || ''}${r.data.paid ? ' (PAID)' : ''}`);
      setStep('done');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to link invoice');
    } finally {
      setLinking(false);
    }
  };

  const handleLinkFromSearch = async (xinv) => {
    // Decide deposit vs final based on amount heuristic
    let invoice_type = 'deposit';
    if (Math.abs((xinv.total || 0) - depositAmount) > Math.abs((xinv.total || 0) - finalAmount)) {
      invoice_type = 'final';
    }
    await linkInvoice({
      xero_invoice_id: xinv.xero_invoice_id,
      invoice_number: xinv.invoice_number,
      amount: xinv.total,
      mark_paid: xinv.status === 'PAID' || (xinv.amount_due === 0 && xinv.total > 0),
      invoice_type,
    });
  };

  const handleManualReconcile = async () => {
    if (!paidInvoiceNumber.trim()) {
      toast.error('Enter the Xero invoice number');
      return;
    }
    await linkInvoice({
      invoice_number: paidInvoiceNumber.trim(),
      amount: parseFloat(paidAmount || (paidInvoiceType === 'deposit' ? depositAmount : finalAmount)) || 0,
      mark_paid: true,
      invoice_type: paidInvoiceType,
    });
  };

  /* ---- Render ---- */
  return (
    <>
      <Dialog open={open && !showXeroDialog} onOpenChange={(o) => !o && closeAll()}>
        <DialogContent className="max-w-2xl" data-testid="post-create-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PoundSterling className="w-5 h-5 text-emerald-600" />
              {step === 'action' && (triggerLabel || `Invoice the customer?`)}
              {step === 'search' && 'Find existing Xero invoice'}
              {step === 'paid' && 'Has the customer paid?'}
              {step === 'reconcile' && 'Record the paid invoice'}
              {step === 'done' && 'All done'}
            </DialogTitle>
            <DialogDescription>
              {customerName} · Deposit £{depositAmount.toLocaleString()} · Final £{finalAmount.toLocaleString()}
            </DialogDescription>
          </DialogHeader>

          {/* STEP 1 — Action */}
          {step === 'action' && (
            <div className="space-y-3 py-2">
              <button
                type="button"
                onClick={handleSendInvoice}
                className="w-full flex items-start gap-3 p-4 border-2 border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50 rounded-lg transition text-left"
                data-testid="action-send-invoice"
              >
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Send className="w-5 h-5 text-emerald-700" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900">Send a {focusLabel.toLowerCase()} invoice now</div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    Create & send a new Xero invoice for £{focusAmount.toLocaleString()}
                    {focusAmount === 0 && <span className="text-amber-600"> — no {focusLabel.toLowerCase()} amount set on this customer</span>}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 self-center" />
              </button>

              <button
                type="button"
                onClick={goToSearch}
                className="w-full flex items-start gap-3 p-4 border-2 border-blue-200 hover:border-blue-400 hover:bg-blue-50 rounded-lg transition text-left"
                data-testid="action-search-xero"
              >
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Search className="w-5 h-5 text-blue-700" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900">Search Xero for an existing invoice</div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    Link an invoice already raised in Xero (deposit or final)
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 self-center" />
              </button>

              <button
                type="button"
                onClick={() => setStep('paid')}
                className="w-full flex items-start gap-3 p-4 border-2 border-slate-200 hover:border-slate-400 hover:bg-slate-50 rounded-lg transition text-left"
                data-testid="action-skip"
              >
                <div className="p-2 bg-slate-100 rounded-lg">
                  <SkipForward className="w-5 h-5 text-slate-700" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900">Skip for now</div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    I'll handle invoicing later
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 self-center" />
              </button>
            </div>
          )}

          {/* SEARCH path */}
          {step === 'search' && (
            <div className="space-y-3 py-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Search by invoice number, reference…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                  data-testid="xero-search-input"
                />
                <Button
                  onClick={() => runSearch()}
                  disabled={searchLoading}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="xero-search-btn"
                >
                  {searchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Showing invoices for this customer's email + matching your search. Click an invoice to link it.
              </p>
              <div className="max-h-[320px] overflow-y-auto border border-slate-200 rounded-md divide-y">
                {searchLoading && (
                  <div className="p-6 text-center text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin inline-block" />
                  </div>
                )}
                {!searchLoading && searchResults.length === 0 && (
                  <div className="p-6 text-center text-slate-400 text-sm">
                    No invoices found yet. Try entering an invoice number above.
                  </div>
                )}
                {!searchLoading && searchResults.map((inv) => (
                  <button
                    key={inv.xero_invoice_id}
                    type="button"
                    disabled={linking}
                    onClick={() => handleLinkFromSearch(inv)}
                    className="w-full text-left p-3 hover:bg-blue-50 transition"
                    data-testid={`xero-invoice-${inv.invoice_number}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <span className="font-semibold text-slate-900 truncate">{inv.invoice_number || '(no number)'}</span>
                          <Badge
                            className={
                              inv.status === 'PAID' ? 'bg-emerald-100 text-emerald-700' :
                              inv.status === 'AUTHORISED' ? 'bg-blue-100 text-blue-700' :
                              inv.status === 'DRAFT' ? 'bg-slate-100 text-slate-700' :
                              'bg-amber-100 text-amber-700'
                            }
                          >
                            {inv.status}
                          </Badge>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {inv.contact_name} · {inv.date}
                          {inv.reference ? ` · ${inv.reference}` : ''}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-slate-900">£{(inv.total || 0).toLocaleString()}</div>
                        {inv.amount_due > 0 && (
                          <div className="text-xs text-amber-600">£{(inv.amount_due || 0).toLocaleString()} due</div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* PAID? */}
          {step === 'paid' && (
            <div className="space-y-3 py-2">
              <p className="text-sm text-slate-600">
                Has {customerName} already paid an invoice (e.g. paid the deposit by bank transfer before being added to the CRM)?
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => handleHasPaid(true)}
                  className="flex flex-col items-center gap-2 p-6 border-2 border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50 rounded-lg transition"
                  data-testid="paid-yes"
                >
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                  <span className="font-semibold">Yes, paid</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleHasPaid(false)}
                  className="flex flex-col items-center gap-2 p-6 border-2 border-slate-200 hover:border-slate-400 hover:bg-slate-50 rounded-lg transition"
                  data-testid="paid-no"
                >
                  <XCircle className="w-8 h-8 text-slate-500" />
                  <span className="font-semibold">No, not yet</span>
                </button>
              </div>
            </div>
          )}

          {/* RECONCILE — pick deposit/final + invoice number */}
          {step === 'reconcile' && (
            <div className="space-y-4 py-2">
              <div>
                <Label className="mb-2 block">Which invoice was paid?</Label>
                <RadioGroup value={paidInvoiceType} onValueChange={setPaidInvoiceType}>
                  <div className="flex items-center gap-2 p-3 border border-slate-200 rounded-md hover:bg-slate-50 cursor-pointer" onClick={() => setPaidInvoiceType('deposit')}>
                    <RadioGroupItem value="deposit" id="rec-deposit" data-testid="reconcile-deposit" />
                    <Label htmlFor="rec-deposit" className="cursor-pointer flex-1">
                      <span className="font-semibold">Deposit invoice</span>
                      <span className="text-slate-500 text-xs ml-2">£{depositAmount.toLocaleString()}</span>
                    </Label>
                  </div>
                  <div className="flex items-center gap-2 p-3 border border-slate-200 rounded-md hover:bg-slate-50 cursor-pointer" onClick={() => setPaidInvoiceType('final')}>
                    <RadioGroupItem value="final" id="rec-final" data-testid="reconcile-final" />
                    <Label htmlFor="rec-final" className="cursor-pointer flex-1">
                      <span className="font-semibold">Final payment invoice</span>
                      <span className="text-slate-500 text-xs ml-2">£{finalAmount.toLocaleString()}</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label htmlFor="rec-invnum">Xero invoice number</Label>
                <Input
                  id="rec-invnum"
                  value={paidInvoiceNumber}
                  onChange={(e) => setPaidInvoiceNumber(e.target.value)}
                  placeholder="INV-0001"
                  data-testid="reconcile-invoice-number"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Used to reconcile this payment back to the right Xero record.
                </p>
              </div>

              <div>
                <Label htmlFor="rec-amount">Amount paid (optional)</Label>
                <Input
                  id="rec-amount"
                  type="number"
                  step="0.01"
                  value={paidAmount}
                  onChange={(e) => setPaidAmount(e.target.value)}
                  placeholder={(paidInvoiceType === 'deposit' ? depositAmount : finalAmount).toString()}
                  data-testid="reconcile-amount"
                />
              </div>
            </div>
          )}

          {/* DONE */}
          {step === 'done' && (
            <div className="space-y-3 py-6 text-center">
              <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
              <p className="font-semibold text-slate-900">Invoice linked successfully</p>
              <p className="text-sm text-slate-600">
                You can manage invoices for {customerName} from the customer detail page.
              </p>
            </div>
          )}

          <DialogFooter>
            {step === 'action' && (
              <Button variant="outline" onClick={closeAll} data-testid="post-create-close">Close</Button>
            )}
            {(step === 'search' || step === 'paid') && (
              <>
                <Button variant="outline" onClick={() => setStep('action')} data-testid="back-to-action">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <Button variant="ghost" onClick={closeAll} data-testid="post-create-close">Close</Button>
              </>
            )}
            {step === 'reconcile' && (
              <>
                <Button variant="outline" onClick={() => setStep('paid')} data-testid="back-to-paid">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <Button
                  onClick={handleManualReconcile}
                  disabled={linking || !paidInvoiceNumber.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="reconcile-confirm"
                >
                  {linking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Mark as paid + link
                </Button>
              </>
            )}
            {step === 'done' && (
              <Button onClick={closeAll} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="post-create-done">Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nested Xero invoice dialog (handles the "Send invoice now" path) */}
      {showXeroDialog && (
        <XeroInvoiceDialog
          open={showXeroDialog}
          onOpenChange={(o) => {
            setShowXeroDialog(o);
            if (!o) {
              // After closing the Xero dialog, finish the post-create flow
              closeAll();
            }
          }}
          customer={customer}
        />
      )}
    </>
  );
};

export default PostCreateInvoiceDialog;
